import type { AuthMeResponseDto, User } from '@up2stay/up2promo-api-client';
import {
  AUTH_STORE_PERSIST_KEY,
  AuthStatus,
  deriveAuthStatus,
  readPersistedAuthTokens,
  type AuthTokens,
} from '@up2stay/up2promo-commons/auth';

import { createHydratedStorage } from './hydrated.store';

export { AuthStatus, type AuthTokens };

/** `/auth/me` payload, or a `User` from login/update/impersonation. */
export type AuthSessionUserInput = AuthMeResponseDto | User;

function isAuthMeResponse(
  user: AuthSessionUserInput,
): user is AuthMeResponseDto {
  return 'smsVerificationAvailable' in user;
}

export function toAuthMeUser(
  user: AuthSessionUserInput,
  previous: AuthMeResponseDto | undefined,
): AuthMeResponseDto {
  if (isAuthMeResponse(user)) {
    return user;
  }

  return {
    ...previous,
    ...user,
    impersonation: undefined,
    smsVerificationAvailable: previous?.smsVerificationAvailable ?? false,
  };
}

interface AuthState {
  tokens?: AuthTokens;
  user?: AuthMeResponseDto;
  authStatus: AuthStatus;
  setTokens: (tokens: AuthTokens | undefined) => void;
  setUser: (user: AuthSessionUserInput | undefined) => void;
  setAuthStatus: (status: AuthStatus) => void;
  logout: () => void;
}

export const useAuthStore = createHydratedStorage<AuthState>(
  (set, get) => ({
    authStatus: AuthStatus.UNKNOWN,
    setTokens: (tokens) =>
      set((state) => {
        const accessToken = tokens?.accessToken;

        if (!accessToken) {
          return {
            ...state,
            tokens: undefined,
            user: undefined,
            authStatus: AuthStatus.UNAUTHENTICATED,
          };
        }

        const hasUser = Boolean(get().user);
        return {
          ...state,
          tokens,
          authStatus: hasUser ? AuthStatus.AUTHENTICATED : AuthStatus.UNKNOWN,
        };
      }),
    setUser: (user) =>
      set((state) => {
        if (!user) {
          return {
            ...state,
            user: undefined,
            authStatus: state.tokens?.accessToken
              ? AuthStatus.UNKNOWN
              : AuthStatus.UNAUTHENTICATED,
          };
        }

        return {
          ...state,
          user: toAuthMeUser(user, state.user),
          authStatus: AuthStatus.AUTHENTICATED,
        };
      }),
    setAuthStatus: (authStatus) => set((state) => ({ ...state, authStatus })),
    logout: () =>
      set({
        tokens: undefined,
        user: undefined,
        authStatus: AuthStatus.UNAUTHENTICATED,
      }),
  }),
  {
    name: AUTH_STORE_PERSIST_KEY,
    // Never persist transient OFFLINE — it sticky-shows the network banner.
    partialize: (state) => ({
      tokens: state.tokens,
      user: state.user,
    }),
    merge: (persistedState, currentState) => {
      const persisted = (persistedState ?? {}) as Partial<AuthState>;
      const tokens = persisted.tokens;
      const user = persisted.user;

      return {
        ...currentState,
        tokens,
        user,
        authStatus: deriveAuthStatus(tokens, user),
      };
    },
  },
);

/**
 * Apply tokens written by another tab. Never clears this tab's session when
 * persist is empty — that path is reserved for explicit logout / refresh 401.
 */
export function applyPersistedAuthTokensIfNewer(): void {
  const persisted = readPersistedAuthTokens();
  if (!persisted) {
    return;
  }

  const current = useAuthStore.getState().tokens;
  if (persisted.refreshToken === current?.refreshToken) {
    return;
  }

  useAuthStore.getState().setTokens(persisted);
}

let crossTabTokenSyncInstalled = false;

export function subscribeAuthStoreToCrossTabTokenSync(): void {
  if (crossTabTokenSyncInstalled || typeof window === 'undefined') {
    return;
  }
  crossTabTokenSyncInstalled = true;

  window.addEventListener('storage', (event) => {
    if (event.key !== AUTH_STORE_PERSIST_KEY || !event.newValue) {
      return;
    }
    applyPersistedAuthTokensIfNewer();
  });
}
